import { useCallback, useEffect, useState } from 'react'
import { FileText, Plus, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchKinds,
  fetchVersions,
  fetchVersion,
  createVersion,
  publishVersion,
  fetchConsentStats,
  KINDS,
  LD_STR as S,
  type KindSummary,
  type DocVersion,
  type VersionDetail,
  type ConsentStats,
} from './legalDocsApi'

export function LegalDocsScreen() {
  const { toast } = useToast()
  const [kinds, setKinds] = useState<KindSummary[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [sel, setSel] = useState<string>('tos')
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchKinds(ac.signal)
      .then(setKinds)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const sumFor = (k: string) => kinds?.find((x) => x.kind === k)

  return (
    <>
      <PageHeader title={S.title} screenId="A24.19" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-4">
          {err && <ErrorState code={err.code} onRetry={reload} />}
          {/* Kind selector */}
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {KINDS.map((k) => {
              const sm = sumFor(k.key)
              const active = sel === k.key
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => setSel(k.key)}
                  className={`rounded-lg border p-3 text-left transition-colors ${active ? 'border-accent bg-surface-2' : 'border-border-default hover:border-border-strong'}`}
                >
                  <div className="flex items-center gap-1.5">
                    <FileText size={14} className="text-tertiary" />
                    <span className="text-sm text-primary">{k.label}</span>
                  </div>
                  <p className="mt-2 text-xs text-tertiary">
                    {sm && sm.published_version > 0 ? (
                      <span className="text-success">v{sm.published_version} {S.published.toLowerCase()}</span>
                    ) : (
                      S.none
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-tertiary">
                    {sm?.versions ?? 0} {S.versions.toLowerCase()} · {sm?.consents ?? 0} {S.consents.toLowerCase()}
                  </p>
                </button>
              )
            })}
          </div>

          {!kinds ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <KindDetail kind={sel} onChanged={reload} />
          )}
        </div>
      </div>
    </>
  )

  function KindDetail({ kind, onChanged }: { kind: string; onChanged: () => void }) {
    const [versions, setVersions] = useState<DocVersion[] | null>(null)
    const [stats, setStats] = useState<ConsentStats | null>(null)
    const [dTick, setDTick] = useState(0)
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [showNew, setShowNew] = useState(false)
    const [busy, setBusy] = useState(false)
    const [viewing, setViewing] = useState<VersionDetail | null>(null)
    const dReload = () => {
      setDTick((t) => t + 1)
      onChanged()
    }

    useEffect(() => {
      const ac = new AbortController()
      fetchVersions(kind, ac.signal).then(setVersions).catch(() => setVersions([]))
      fetchConsentStats(kind, ac.signal).then(setStats).catch(() => setStats(null))
      return () => ac.abort()
    }, [kind, dTick])

    const create = async () => {
      if (!title.trim()) return
      setBusy(true)
      try {
        await createVersion(kind, title.trim(), content)
        setTitle('')
        setContent('')
        setShowNew(false)
        toast(S.done, { tone: 'success' })
        dReload()
      } catch {
        toast(S.failed, { tone: 'error' })
      } finally {
        setBusy(false)
      }
    }
    const publish = async (id: number) => {
      setBusy(true)
      try {
        await publishVersion(id)
        toast(S.done, { tone: 'success' })
        dReload()
      } catch {
        toast(S.failed, { tone: 'error' })
      } finally {
        setBusy(false)
      }
    }
    const view = async (id: number) => {
      try {
        setViewing(await fetchVersion(id))
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    }

    return (
      <div className="space-y-3">
        {/* Consent adoption */}
        {stats && stats.published_version > 0 && (
          <div className="rounded-lg border border-border-default bg-surface-2 px-4 py-2 text-sm text-secondary">
            {S.adoption} (v{stats.published_version}): <b className="text-primary">{stats.current_version_consents}</b> {S.of} {stats.total_consents}
          </div>
        )}

        {/* New version */}
        {showNew ? (
          <div className="space-y-2 rounded-lg border border-border-default p-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={S.docTitle}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={S.content}
              rows={8}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 font-mono text-sm text-primary outline-none focus:border-accent"
            />
            <div className="flex gap-2">
              <button type="button" onClick={create} disabled={busy || !title.trim()} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">
                {S.create}
              </button>
              <button type="button" onClick={() => setShowNew(false)} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">
                {S.close}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setShowNew(true)} className="flex items-center gap-1 rounded-md border border-dashed border-border-default px-3 py-2 text-sm text-secondary hover:text-primary">
            <Plus size={14} />
            {S.newVersion}
          </button>
        )}

        {/* Versions */}
        {!versions ? (
          <Skeleton className="h-10 w-full" />
        ) : versions.length === 0 ? (
          <p className="py-6 text-center text-sm text-tertiary">{S.noVersions}</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border-default">
            {versions.map((v, i) => (
              <div key={v.id} className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                <span className="font-mono text-sm text-tertiary">v{v.version}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-primary">{v.title || '—'}</p>
                  <p className="text-xs text-tertiary">{v.length} {S.chars} · {new Date(v.created_at).toLocaleDateString()}</p>
                </div>
                {v.published ? (
                  <span className="rounded bg-success/10 px-2 py-0.5 text-xs text-success">{S.published}</span>
                ) : (
                  <button type="button" onClick={() => publish(v.id)} disabled={busy} className="rounded-md bg-accent px-2.5 py-1 text-xs text-white disabled:opacity-50">
                    {S.publish}
                  </button>
                )}
                <button type="button" onClick={() => view(v.id)} className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary">
                  {S.view}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* View modal */}
        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setViewing(null)}>
            <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border border-border-default bg-surface-1 shadow-lg" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-border-default px-4 py-2.5">
                <span className="text-sm font-medium text-primary">{viewing.title} · v{viewing.version}</span>
                <button type="button" onClick={() => setViewing(null)} className="text-tertiary hover:text-primary">
                  <X size={16} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap p-4 text-sm text-secondary">{viewing.content || '—'}</div>
            </div>
          </div>
        )}
      </div>
    )
  }
}
